import { useCallback, MouseEvent } from 'react';
import { useHistory } from 'react-router-dom';
import { captureException } from '@sentry/react';
import { useMutation } from '@apollo/client';
import { UPDATE_CHANNEL } from 'pages/Conversations/graphql/mutations';
import { ChannelStatusAction, useGetOrCreateActiveClientChannelMutation } from 'shared/types/graphql';
import { replaceParam, RouteDefinitions } from 'routes';

interface UseAppointmentChannelProps {
  appointmentId?: string;
  channelId?: string | null;
  channelStatusAction?: ChannelStatusAction;
  petParentIds: string[];
  clinicPetId?: string;
}

export default function useAppointmentChannel({
  appointmentId,
  channelId,
  channelStatusAction,
  petParentIds,
  clinicPetId,
}: UseAppointmentChannelProps): { onChatClick: (e: MouseEvent<HTMLButtonElement>) => void } {
  const history = useHistory();
  const [getOrCreateActiveClientChannel] = useGetOrCreateActiveClientChannelMutation();
  const [updateChannel] = useMutation(UPDATE_CHANNEL);

  const gotoChannel = useCallback(
    (channelId: string): void => {
      history.push(`${replaceParam(RouteDefinitions.Conversations, ':channelId', channelId)}`, {
        linkPatientsToConversation: [clinicPetId],
      });
    },
    [clinicPetId, history],
  );

  const onChatClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>): void => {
      e.stopPropagation();
      if (!appointmentId) return;
      if (channelId && channelStatusAction === ChannelStatusAction.Active) {
        gotoChannel(channelId);
      } else if (petParentIds.length) {
        getOrCreateActiveClientChannel({
          variables: { data: { clinicPetParentIds: petParentIds } },
        })
          .then((resp) => {
            const newChannelId = resp.data?.getOrCreateActiveClientChannel?.id;
            updateChannel({
              variables: {
                data: { appointments: { connect: { id: appointmentId } } },
                where: { id: newChannelId },
              },
            });
            if (newChannelId) {
              gotoChannel(newChannelId);
            }
          })
          .catch((error) => {
            console.error(error);
            captureException(error);
          });
      }
    },
    [
      channelId,
      channelStatusAction,
      petParentIds,
      appointmentId,
      gotoChannel,
      getOrCreateActiveClientChannel,
      updateChannel,
    ],
  );

  return { onChatClick };
}
